import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { getAuthBackgroundForRole, getAuthRoleConfig, normalizeAuthRole, readAuthBackgrounds } from '../../utils/authVisualConfig';
import { readBrandingVisual } from '../../utils/brandingVisualConfig';
import './AuthLayout.css';

const AuthLayout = ({
  mode = 'login',
  roleType = 'client',
  stepLabel,
  title,
  highlight,
  subtitle,
  children,
  compact = false
}) => {
  const normalizedRole = normalizeAuthRole(roleType);
  const roleConfig = getAuthRoleConfig(normalizedRole);
  const [backgrounds, setBackgrounds] = useState(() => readAuthBackgrounds());
  const [brandingVisual, setBrandingVisual] = useState(() => readBrandingVisual());

  useEffect(() => {
    const onStorage = () => {
      setBackgrounds(readAuthBackgrounds());
      setBrandingVisual(readBrandingVisual());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('provi:auth-backgrounds-updated', onStorage);
    window.addEventListener('provi:branding-visual-updated', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('provi:auth-backgrounds-updated', onStorage);
      window.removeEventListener('provi:branding-visual-updated', onStorage);
    };
  }, []);

  const bg = getAuthBackgroundForRole(normalizedRole, backgrounds);
  const activeTitle = title || roleConfig[`${mode}Title`] || roleConfig.loginTitle;
  const activeHighlight = highlight || roleConfig[`${mode}Highlight`] || roleConfig.loginHighlight;
  const activeSubtitle = subtitle || roleConfig.subtitle;
  const useImage = Boolean(bg.image_url);
  const authLogoUrl = brandingVisual.auth_logo_url || brandingVisual.logo_icon_url;

  return (
    <main className={`provi-auth-page ${compact ? 'compact' : ''}`}>
      <div className="provi-auth-grid-bg" />
      <div className="provi-auth-orb one" />
      <div className="provi-auth-orb two" />
      {useImage && bg.show_desktop && (
        <div
          className="provi-auth-photo desktop-only"
          style={{ backgroundImage: `url(${bg.image_url})`, opacity: Math.min(Number(bg.opacity) || 0.16, 0.35) }}
        />
      )}
      {useImage && bg.show_mobile && (
        <div
          className="provi-auth-photo mobile-only"
          style={{ backgroundImage: `url(${bg.image_url})`, opacity: Math.min(Number(bg.opacity) || 0.1, 0.12) }}
        />
      )}

      <section className="provi-auth-shell">
        <aside className="provi-auth-hero">
          <div className="provi-auth-brand">
            <div className="provi-auth-logo">
              {authLogoUrl ? <img src={authLogoUrl} alt="PROVI" /> : <Sparkles size={24} />}
            </div>
            <strong>PROVI</strong>
          </div>

          <div className="provi-auth-hero-content">
            <div className="provi-auth-eyebrow">{roleConfig.eyebrow}</div>
            <h1>{activeTitle}<span>{activeHighlight}</span></h1>
            <p>{activeSubtitle}</p>
            <div className="provi-auth-chips">
              {roleConfig.chips.map((chip) => <span key={chip}>{chip}</span>)}
            </div>
          </div>

          <div className="provi-auth-map" aria-hidden="true">
            <svg viewBox="0 0 700 240" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M80 160 C170 80, 235 205, 330 135 C430 60, 500 160, 610 80" stroke="url(#authRouteGradient)" strokeWidth="5" strokeLinecap="round" strokeDasharray="12 13" />
              <defs>
                <linearGradient id="authRouteGradient" x1="80" y1="160" x2="610" y2="80" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#FF007F" />
                  <stop offset=".5" stopColor="#8A2BE2" />
                  <stop offset="1" stopColor="#00F0FF" />
                </linearGradient>
              </defs>
            </svg>
            <span className="pin a" />
            <span className="pin b" />
            <div className="provi-auth-map-badge">{roleConfig.mapBadge}</div>
          </div>
        </aside>

        <section className="provi-auth-form-wrap">
          <div className="provi-auth-form-card">
            {stepLabel && <div className="provi-auth-step">● {stepLabel}</div>}
            {children}
          </div>
          <Link to="/" className="provi-auth-back">Volver al inicio</Link>
        </section>
      </section>
    </main>
  );
};

export default AuthLayout;
